import '@awesome.me/webawesome/dist/components/tab/tab.js';
import '@awesome.me/webawesome/dist/components/tab-group/tab-group.js';
import '@awesome.me/webawesome/dist/components/tab-panel/tab-panel.js';
import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '../components/access_level_badge.js';
import '../components/breadcrumb.js';
import '../components/counter_badge.js';
import '../components/empty_state.js';
import '../components/error_state.js';
import '../components/farm_access.js';
import '../components/farm_detach.js';
import '../components/farm_fertilizer_years.js';
import '../components/farm_invitations.js';
import '../components/farm_master_data.js';
import '../components/farm_transfer.js';
import '../libraries/events.js';

import { Task, TaskStatus } from '@lit/task';
import { html, LitElement, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';

import {
    getOrganization,
    type IOrganization,
} from '../libraries/api_client/organization.js';
import type {
    ApiResult,
} from '../libraries/api_client/request.js';
import type { DetailMode } from '../libraries/detail_mode.js';
import {
    detailPageHeading,
    detailPageNoAccess,
    detailPageStyles,
    detailPageTabs,
    detailPageTabStyles,
    type IDetailPageTab,
} from '../libraries/detail_page.js';
import { farmErrorCopy } from '../libraries/farm_copy.js';
import { farmPath, farmsPath } from '../libraries/farm_paths.js';
import { masterDataLoadError } from '../libraries/master_data_edit.js';
import { MasterDataReport } from '../libraries/master_data_report.js';
import { redirectTo } from '../libraries/navigation.js';
import {
    canDetachFarm,
    canInviteToFarm,
    canTransferFarm,
    canViewFarmAccess,
    canWriteFarm,
    getFarmRolePreset,
    permissionsChangedEventName,
} from '../libraries/permissions.js';
import { skeletonStyles } from '../libraries/skeletons.js';
import type { ITabLeaf } from '../libraries/tab_leaf.js';
import { settledAccess, TabLeafOrchestration } from '../libraries/tab_leaf_orchestration.js';
import { resolveEffectiveTab } from '../libraries/tabs.js';
import { visuallyHiddenStyles } from '../libraries/visually_hidden_styles.js';
import { onWindowEvent, WindowEventsController } from '../libraries/window_events.js';

export type FarmDetailTab =
    | 'master-data' | 'fertilizer-years' | 'invitations' |
    'farm-access' | 'transfer' | 'detach';

// Suffix appended to the farm base path for each tab. `master-data` is
// the base path itself, so its suffix is the empty string. Being a
// `Record<FarmDetailTab, …>` it is also the exhaustive tab list, so
// iterating its keys cannot miss a tab a future change adds.
const tabPathSuffix: Record<FarmDetailTab, string> = {
    'master-data': '',
    'fertilizer-years': '/fertilizer-years',
    'invitations': '/invitations',
    'farm-access': '/farm-access',
    'transfer': '/transfer',
    'detach': '/detach',
};

/**
 * Type guard for `FarmDetailTab` strings off the `wa-tab-show` event.
 * @param name - The candidate name from the tab event.
 * @returns Whether `name` is a declared `FarmDetailTab`.
 */
function isFarmDetailTab (name: string): name is FarmDetailTab {
    return name in tabPathSuffix;
}

interface IAccess {
    canWrite: boolean;
    canViewAccess: boolean;
    canTransfer: boolean;
    canDetach: boolean;
    canInvite: boolean;
    rolePreset: string | null;
}

const denyAccess: IAccess = {
    canWrite: false,
    canViewAccess: false,
    canTransfer: false,
    canDetach: false,
    canInvite: false,
    rolePreset: null,
};

/** The document title of the Stammdaten tab, as its route names it. */
const masterDataTitle = 'Stammdaten des Betriebs';

/**
 * Farm-Detail page. Owns identity (`organizationId`, `farmId`, `tab`),
 * the access snapshot, the tab strip, and orchestration that drives
 * each leaf's `refresh()`. Page-internal data fetching lives entirely
 * in the leaves; this page never touches the network for farm /
 * leadership / years / transfer / organisation itself. The display
 * name for the breadcrumb + heading is sourced from the master-data
 * leaf via the `fm-display-name-changed` event.
 *
 * Tab visibility has a single source, `tabVisibility`: invitations are
 * conditional on `access.canInvite` (`farm:manage` ∨ the owner), farm-access on
 * `access.canViewAccess` (`organization:manage` on the route organization ∨
 * `farm:manage` ∨ the owner, read-only), transfer on `access.canTransfer`, and
 * detach on `access.canDetach` plus at least one organization assignment. The
 * owner counts while they hold their direct grant, leading the farm or not.
 * Both assignment axes count, so a farm held by a consulting organization
 * alone can still be left; only a farm without any assignment has nothing
 * to leave. The URL's organization segment must never gate a tab, because
 * every farm list links a farm without a managing organisation as
 * `/farms/:farmId`.
 *
 * `effectiveTab` falls back to `master-data` whenever the tab named by
 * the URL is not visible, so a deep link to a gated tab renders the
 * default tab instead of an empty panel. The URL is deliberately left
 * untouched, and leaf orchestration follows the same effective tab.
 *
 * On a 404 / 403 primary-load failure reported by the master-data leaf through
 * `fm-display-name-failed`, the page short-circuits to a full-page
 * `<fm-error-state>`. Other statuses fall through to the leaf-level callout so
 * the page chrome stays intact.
 */
@customElement('fm-farm-detail')
export class FmFarmDetail extends LitElement {

    static styles = [
        detailPageStyles,
        detailPageTabStyles,
        skeletonStyles,
        visuallyHiddenStyles,
    ];

    @property({
        type: String,
    })
    organizationId = '';

    @property({
        type: String,
    })
    farmId = '';

    @property({
        type: String,
    })
    tab: FarmDetailTab = 'master-data';

    @property({
        type: String,
    })
    mode: DetailMode = 'view';

    @state()
    private pendingInvitationsCount = 0;

    @state()
    private permissionsVersion = 0;

    /**
     * Whether the farm holds at least one organisation assignment,
     * managing or consulting, as last reported by the master-data leaf.
     * Optimistic `true` until that report arrives, which stays
     * unobservable in the regular flow because the tab strip hides
     * behind its skeleton until the very same event lands; on a failed
     * leaf load the tab stays offered and the server remains the
     * authority for the detach itself.
     */
    @state()
    private farmHasOrganizationAssignment = true;

    /** The Betrieb's name and load failure, as the master-data leaf reports them. */
    private readonly masterData = new MasterDataReport(this, {
        entityId: (): string => this.farmId,
        errorCopy: farmErrorCopy,
        accessStatus: (): TaskStatus => this.accessTask.status,
        onReported: (detail): void => {
            this.farmHasOrganizationAssignment = detail.hasOrganizationAssignment ?? true;
        },
    });

    private lastBootstrappedKey: string | null = null;

    private get effectiveOrganizationId (): string | null {
        return this.organizationId.length > 0 ? this.organizationId : null;
    }

    /**
     * The access snapshot every consumer reads: the tab strip, the panels, `tabVisibility`, and
     * through it the effective tab and the leaf orchestration.
     * @returns The access snapshot to gate on.
     */
    private get currentAccess (): IAccess {
        return settledAccess(this.accessTask, denyAccess);
    }

    /**
     * The single source of tab visibility. Anything that needs to know
     * whether a tab exists reads this: the tab strip, the panel bodies,
     * and `effectiveTab`.
     * @returns Visibility per tab.
     */
    private get tabVisibility (): Record<FarmDetailTab, boolean> {
        const access = this.currentAccess;

        return {
            'master-data': true,
            'fertilizer-years': true,
            'invitations': access.canInvite,
            'farm-access': access.canViewAccess,
            'transfer': access.canTransfer,
            'detach': access.canDetach && this.farmHasOrganizationAssignment,
        };
    }

    /**
     * The tab actually rendered, which is the tab named by the URL as
     * long as it is visible and `master-data` otherwise. The URL is not
     * rewritten, so a link to a tab the user regains access to keeps
     * working.
     * @returns The effective tab.
     */
    private get effectiveTab (): FarmDetailTab {
        return resolveEffectiveTab(this.tab, this.tabVisibility, 'master-data');
    }

    private readonly accessTask = new Task(this, {
        task: async ([
            farmId, organizationId,
        ]): Promise<IAccess> => {
            if (farmId === '') {
                return denyAccess;
            }

            const [
                canWrite,
                canViewAccess,
                canTransfer,
                canDetach,
                canInvite,
                rolePreset,
            ] = await Promise.all([
                canWriteFarm(farmId),
                canViewFarmAccess(farmId, organizationId === '' ? null : organizationId),
                canTransferFarm(farmId),
                canDetachFarm(farmId),
                canInviteToFarm(farmId),
                getFarmRolePreset(farmId),
            ]);

            return {
                canWrite,

                // The Betriebszugriff tab's gate; see `canViewFarmAccess`. The
                // master-data leaf resolves the gates needing the MANAGING axis
                // itself (`canSetFarmActivation`, `canTransferLeadership`).
                canViewAccess,
                canTransfer,
                canDetach,
                canInvite,
                rolePreset,
            };
        },
        args: (): readonly [string, string, number] => [
            this.farmId,
            this.organizationId,
            this.permissionsVersion,
        ],
        autoRun: true,
    });

    private readonly leaves = new TabLeafOrchestration<FarmDetailTab>(this, {
        refs: {
            'master-data': createRef<ITabLeaf>(),
            'fertilizer-years': createRef<ITabLeaf>(),
            'invitations': createRef<ITabLeaf>(),
            'farm-access': createRef<ITabLeaf>(),
            'transfer': createRef<ITabLeaf>(),
            'detach': createRef<ITabLeaf>(),
        },
        requestedTab: (): FarmDetailTab => this.tab,
        effectiveTab: (): FarmDetailTab => this.effectiveTab,
        fallbackTab: 'master-data',
        fallbackTitle: masterDataTitle,
        accessStatus: (): TaskStatus => this.accessTask.status,
    });

    /**
     * Resolves the organisation name for the parent breadcrumb segment
     * on org-scoped routes. Returns `null` for orphan farms so the
     * renderer falls back to the generic "Alle" label. On API failure
     * the renderer falls back to the generic "Organisation" label so
     * the parent link stays discoverable. The Task is keyed on
     * `effectiveOrganizationId` so it re-runs automatically when the
     * page transitions between organisations or between org-scoped
     * and orphan routes.
     */
    private readonly organizationTask = new Task(this, {
        task: async ([
            organizationId,
        ], { signal }): Promise<ApiResult<IOrganization> | null> => {
            if (organizationId === null) {
                return null;
            }

            return getOrganization(organizationId, {
                signal,
            });
        },
        args: (): readonly [string | null] => [
            this.effectiveOrganizationId,
        ],
        autoRun: true,
    });

    private readonly windowEvents = new WindowEventsController(this, [
        onWindowEvent(permissionsChangedEventName, () => {
            this.permissionsVersion += 1;
        }),
    ]);

    private readonly handleInvitationsCountChanged = (
        event: HTMLElementEventMap['fm-count-changed']
    ): void => {
        this.pendingInvitationsCount = event.detail.count;
    };

    private get bootstrapKey (): string {
        return `${this.organizationId}|${this.farmId}`;
    }

    override willUpdate (changedProperties: PropertyValues<this>): void {
        const identityChanged =
            changedProperties.has('organizationId') ||
            changedProperties.has('farmId');

        if (identityChanged && this.bootstrapKey !== this.lastBootstrappedKey) {
            this.lastBootstrappedKey = this.bootstrapKey;
            this.farmHasOrganizationAssignment = true;
            this.masterData.restart();
            this.leaves.restart();
        }
    }

    private readonly handleTabShow = (event: CustomEvent<{ name: string }>): void => {
        const { name } = event.detail;

        // Compared against the effective tab, not the URL's: a tab group
        // announces the tab it activates programmatically, and on a
        // fallback deep-link that is the default tab, which must not
        // rewrite the URL the user opened.
        if (!isFarmDetailTab(name) || name === this.effectiveTab) {
            return;
        }

        redirectTo(this.tabUrl(name));
    };

    private buildFarmBasePath (): string {
        return farmPath(this.effectiveOrganizationId, this.farmId);
    }

    private tabUrl (name: FarmDetailTab): string {
        return `${this.buildFarmBasePath()}${tabPathSuffix[name]}`;
    }

    private get headerRolePreset (): string | null {
        return this.accessTask.value?.rolePreset ?? null;
    }

    private renderHeader (): TemplateResult {
        const orgScoped = this.effectiveOrganizationId !== null;
        const parentHref = orgScoped ?
            `/organizations/${this.effectiveOrganizationId}/farms` :
            '/organizations';

        const parentLabel = orgScoped ? this.resolveOrganizationLabel() : 'Alle';
        const label = this.masterData.displayName ?? 'Betrieb';

        return detailPageHeading({
            pending: this.masterData.headingPending,
            items: [
                {
                    label: parentLabel,
                    href: parentHref,
                },
                {
                    label,
                },
            ],
            label,
            metadata: html`
                <fm-access-level-badge .rolePreset=${this.headerRolePreset}>
                </fm-access-level-badge>
            `,
        });
    }

    /**
     * Resolves the parent breadcrumb label for org-scoped routes from
     * the organisation Task. Pending → non-breaking space so the
     * existing visual stays stable while the fetch is in flight.
     * Complete with a successful API response → the organisation name.
     * Complete with an API failure or Task error → the generic
     * "Organisation" label so the link target stays discoverable.
     * @returns The label to render in the parent breadcrumb segment.
     */
    private resolveOrganizationLabel (): string {
        const { status, value } = this.organizationTask;

        if (status === TaskStatus.ERROR) {
            return 'Organisation';
        }

        if (status !== TaskStatus.COMPLETE || value === null || value === undefined) {
            return '\u00A0';
        }

        if (value.success) {
            return value.value.nameWithLegalForm;
        }

        return 'Organisation';
    }

    /**
     * Whether the managing organisation is currently active, propagated to
     * the master-data leaf for its "Aktivieren" gate. Optimistic `true` until
     * the organisation fetch resolves, and `true` for orphan farms (no
     * managing organisation, so the task returns `null`); the leaf only
     * disables reactivation for a farm that has a managing organisation, and
     * the server enforces the freeze regardless.
     * @returns Whether the managing organisation is active.
     */
    private get managingOrganizationActive (): boolean {
        const { value } = this.organizationTask;

        return value?.success === true ? value.value.active : true;
    }

    /**
     * The tabs this farm offers. Stammdaten and Düngejahre are always offered,
     * which is why they name no visibility; the rest are withheld by
     * {@link FmFarmDetail.tabVisibility}.
     * @returns The tab descriptors.
     */
    private get detailTabs (): ReadonlyArray<IDetailPageTab> {
        const visible = this.tabVisibility;

        return [
            {
                name: 'master-data',
                label: 'Stammdaten',
            },
            {
                name: 'fertilizer-years',
                label: 'Düngejahre',
            },
            {
                name: 'invitations',
                label: 'Einladungen',
                count: this.pendingInvitationsCount,
                visible: visible.invitations,
            },
            {
                name: 'farm-access',
                label: 'Betriebszugriff',
                visible: visible['farm-access'],
            },
            {
                name: 'transfer',
                label: 'Umzug',
                visible: visible.transfer,
            },
            {
                name: 'detach',
                label: 'Auszug',
                visible: visible.detach,
            },
        ];
    }

    private renderTabStrip (access: IAccess): TemplateResult {
        return detailPageTabs({
            tabs: this.detailTabs,
            active: this.effectiveTab,
            pending: this.masterData.tabsPending,
            onTabShow: this.handleTabShow,
            panels: this.renderPanels(access),
        });
    }

    private renderPanels (access: IAccess): TemplateResult {
        return html`
            ${this.renderMasterDataPanel(access)}
            ${this.renderFertilizerYearsPanel(access)}
            ${this.renderInvitationsPanel()}
            ${this.renderFarmAccessPanel()}
            ${this.renderTransferPanel(access)}
            ${this.renderDetachPanel(access)}
        `;
    }

    private renderMasterDataPanel (access: IAccess): TemplateResult {
        return html`
            <wa-tab-panel name="master-data">
                <fm-farm-master-data
                        ${ref(this.leaves.refs['master-data'])}
                        .organizationId=${this.organizationId}
                        .farmId=${this.farmId}
                        .canWrite=${access.canWrite}
                        .organizationActive=${this.managingOrganizationActive}
                        .mode=${this.mode}
                        @fm-display-name-changed=${this.masterData.handleChanged}
                        @fm-display-name-failed=${this.masterData.handleFailed}>
                </fm-farm-master-data>
            </wa-tab-panel>
        `;
    }

    private renderFertilizerYearsPanel (access: IAccess): TemplateResult {
        return html`
            <wa-tab-panel name="fertilizer-years">
                <fm-farm-fertilizer-years
                        ${ref(this.leaves.refs['fertilizer-years'])}
                        .organizationId=${this.organizationId}
                        .farmId=${this.farmId}
                        .canWrite=${access.canWrite}>
                </fm-farm-fertilizer-years>
            </wa-tab-panel>
        `;
    }

    private renderInvitationsPanel (): TemplateResult {
        const visible = this.tabVisibility.invitations;

        return html`
            <wa-tab-panel name="invitations">
                ${visible ? html`
                    <fm-farm-invitations
                            ${ref(this.leaves.refs.invitations)}
                            .farmId=${this.farmId}
                            @fm-count-changed=${this.handleInvitationsCountChanged}>
                    </fm-farm-invitations>
                ` : detailPageNoAccess()}
            </wa-tab-panel>
        `;
    }

    private renderFarmAccessPanel (): TemplateResult {
        const visible = this.tabVisibility['farm-access'];

        return html`
            <wa-tab-panel name="farm-access">
                ${visible ? html`
                    <fm-farm-access
                            ${ref(this.leaves.refs['farm-access'])}
                            .organizationId=${this.organizationId}
                            .farmId=${this.farmId}>
                    </fm-farm-access>
                ` : detailPageNoAccess()}
            </wa-tab-panel>
        `;
    }

    private renderTransferPanel (access: IAccess): TemplateResult {
        return html`
            <wa-tab-panel name="transfer">
                ${this.tabVisibility.transfer ? html`
                    <fm-farm-transfer
                            ${ref(this.leaves.refs.transfer)}
                            .organizationId=${this.organizationId}
                            .farmId=${this.farmId}
                            .canTransfer=${access.canTransfer}>
                    </fm-farm-transfer>
                ` : detailPageNoAccess()}
            </wa-tab-panel>
        `;
    }

    private renderDetachPanel (access: IAccess): TemplateResult {
        return html`
            <wa-tab-panel name="detach">
                ${this.tabVisibility.detach ? html`
                    <fm-farm-detach
                            ${ref(this.leaves.refs.detach)}
                            .farmId=${this.farmId}
                            .canDetach=${access.canDetach}>
                    </fm-farm-detach>
                ` : detailPageNoAccess()}
            </wa-tab-panel>
        `;
    }

    render (): TemplateResult {
        const errorState = this.masterData.errorState(farmsPath(this.effectiveOrganizationId));

        if (errorState !== null) {
            return errorState;
        }

        return html`
            ${this.renderHeader()}
            ${this.renderMasterDataFailure()}
            ${this.renderTabStrip(this.currentAccess)}
        `;
    }

    /**
     * The master-data leaf's load failure, said above whichever other tab is open; on the
     * Stammdaten tab the leaf's own callout says it.
     * @returns The callout, or nothing.
     */
    private renderMasterDataFailure (): TemplateResult | typeof nothing {
        return masterDataLoadError(
            this.masterData.loadFailed && this.effectiveTab !== 'master-data',
            (): void => {
                void this.leaves.refs['master-data'].value?.refresh();
            }
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-farm-detail': FmFarmDetail;
    }
}
